import { zodResolver } from '@hookform/resolvers/zod';
import { HTTPError } from 'ky';
import { Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useEmailVerified } from '@/hooks/useEmailVerified';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { toast } from '@/lib/notify';

import { useGetCurrentTenant } from '../api/useGetCurrentTenant';
import { useUpdateCurrentTenant } from '../api/useUpdateCurrentTenant';
import { type OrgSettingsInput, orgSettingsSchema } from '../schemas/settingsSchemas';

// Right card on the Settings page. Two sections: Organization Settings
// (name + description) and Branding (background color + logo upload).
//
// Data flow:
//   * useGetCurrentTenant — TanStack Query against GET /api/v1/tenants/me.
//     Returns `null` (not an error) when the caller has no tenant
//     attachment, so the card unmounts cleanly for platform-only users.
//   * useUpdateCurrentTenant — PATCH /api/v1/tenants/me. Backend gates
//     PATCH behind Tenant.Edit; Editor + Viewer roles get a 403 we
//     surface as a toast.
//
// Logo upload is preview-only -- the file-storage endpoint isn't wired
// yet. Selecting a file shows a thumbnail with a remove button; saving
// the form ignores the logo field for now.
export function OrganizationCard() {
  const isEmailVerified = useEmailVerified();
  const hasPermission = useMyPermissions();
  const tenantQuery = useGetCurrentTenant();
  const updateTenant = useUpdateCurrentTenant();

  const disableReason = !isEmailVerified
    ? 'Verify your email to make changes to organization settings.'
    : !hasPermission('Tenant.Edit')
      ? 'You do not have permission to edit organization settings.'
      : null;
  const canEdit = disableReason === null;

  const tenant = tenantQuery.data;

  const [logo, setLogo] = useState<{ file: File; previewUrl: string } | null>(null);

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OrgSettingsInput>({
    resolver: zodResolver(orgSettingsSchema),
    defaultValues: { name: '', description: '' },
  });

  useEffect(() => {
    if (!tenant) return;
    reset({ name: tenant.name, description: tenant.description ?? '' });
  }, [tenant, reset]);

  // Don't render the card at all for platform-only users (404 -> null).
  // Loading state is also "no card yet" to avoid a flicker; cheap because
  // the Settings page already has the profile card filling the left side.
  if (tenant === null || tenantQuery.isLoading) {
    return null;
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await updateTenant.mutateAsync({
        name: values.name,
        description: values.description || null,
      });
      toast.success('Settings saved');
    } catch (error) {
      if (error instanceof HTTPError && error.response.status === 403) {
        toast.error('You do not have permission to update organization settings.');
        return;
      }
      toast.error(extractErrorMessage(error, 'Could not save settings.'));
    }
  });

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (logo) {
      URL.revokeObjectURL(logo.previewUrl);
    }
    setLogo({ file, previewUrl: URL.createObjectURL(file) });
  };

  const handleLogoRemove = () => {
    if (logo) {
      URL.revokeObjectURL(logo.previewUrl);
    }
    setLogo(null);
  };

  return (
    <div className="w-full space-y-4">
      <div className="border border-surface-soft-border rounded-[20px] p-4 lg:p-5 text-black-logo">
        <div className="flex flex-col gap-4">
          <h5 className="text-base lg:text-lg lg:leading-[22px] font-semibold">
            Organization Settings
          </h5>
          <div className="w-full h-px bg-grayEF" />

          <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="org-name" className="sc-input-label text-black">
                Organization Name
              </label>
              <input
                id="org-name"
                type="text"
                className="sc-input disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="Organization Name"
                disabled={!canEdit}
                {...register('name')}
              />
              {errors.name && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="org-description" className="sc-input-label text-black">
                Description
              </label>
              <textarea
                id="org-description"
                className="sc-textarea min-h-[90px] disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="Type Here..."
                disabled={!canEdit}
                {...register('description')}
              />
              {errors.description && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div className="w-full h-px bg-grayEF my-4" />

            <div className="flex flex-col gap-1">
              <label htmlFor="org-logo" className="sc-input-label text-black">
                Organization Logo
              </label>
              <div className="upload-wrapper w-full">
                <label
                  htmlFor="org-logo"
                  className="relative flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-placeholder p-2.5 text-center cursor-pointer transition-colors hover:border-primary"
                >
                  <div className="h-7 w-7 flex items-center justify-center rounded-full bg-icon-surface text-placeholder">
                    <Upload className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm leading-[150%] font-medium tracking-tight text-grayA5">
                      Drag and Drop or <span className="text-primary">Click to upload</span>
                    </p>
                    <p className="text-[10px] leading-[150%] font-medium tracking-tight text-grayA5">
                      Recommended size: 512x512px. Max file size: 2MB.
                    </p>
                    <p className="text-[10px] leading-[150%] font-medium tracking-tight text-grayA5">
                      Supported formats: PNG, JPG, SVG.
                    </p>
                  </div>
                  <input
                    id="org-logo"
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    onChange={handleLogoChange}
                    disabled={!canEdit}
                    className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  />
                </label>
                {logo && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <div className="w-15 h-15 shrink-0 rounded-md overflow-hidden relative bg-placeholder/15">
                      <button
                        type="button"
                        onClick={handleLogoRemove}
                        aria-label="Remove logo"
                        className="cursor-pointer w-4 h-4 rounded-full bg-destructive text-white top-1 end-1 absolute flex items-center justify-center text-[10px]"
                      >
                        ×
                      </button>
                      <img
                        src={logo.previewUrl}
                        alt="Selected logo preview"
                        className="object-cover h-full w-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {disableReason && (
                <p className="text-sm text-gray66 max-lg:text-center">{disableReason}</p>
              )}
              <button
                type="submit"
                className="btn btn-primary px-2.5! max-lg:mx-auto lg:ms-auto disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || updateTenant.isPending || !canEdit}
              >
                {updateTenant.isPending ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}
